import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { organizationsService } from '../../api/organizationService';
// import { Column } from '../../types/types';
import { contactService } from '../../api/contactService';
import { StyledCustomFieldBar } from '../../features/contacts/ContactOverview/ContactCustomFields';
import DnDIcon from '../../assets/icons/DnDIcon';
import StyledText from '../StyledComponents/StyledText';
import { CorporateTheme } from '../../utils/themes';
import CloseIcon from '../../assets/icons/CloseIcon';
import { Button } from '../form/Button';
import { SkeletonCell } from '../SkeletonCell';
import { formatColumnLabel } from '../../utils/helper';
import DocumentIcon from '../../assets/icons/document-text';
import { useTheme } from '../../middleware/ThemeContext';
import { ReactComponent as NoDataIcon } from "../../assets/images/NOData.svg";
import { debounce } from 'lodash';
import SaveIcon from '../../assets/icons/SaveIcon';

const Container = styled.div`
  display: flex;
  padding: 1rem;
  gap: 2rem;
  border: 1px solid ${CorporateTheme.colors.border};
  border-radius: ${CorporateTheme.borderRadius};
`;

const HeaderContainer = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 2rem 2rem;
  border-radius: ${CorporateTheme.borderRadius};
  overflow: hidden;

  &::before {
    content: "";
    background-image: url(${require("../../assets/images/CreateFormBackground.png")});
    background-size: cover;
    background-position: center;
    position: absolute;
    inset: 0;
    z-index: 0;
    opacity: 0.9;
  }

  * {
    position: relative;
    z-index: 1;
  }
`;

const HeaderTitles = styled.div`
  text-align: center;
  max-width: 520px;
  font-family: "Raleway", sans-serif;
`;

const LeftPanel = styled.div(({ theme }) => ({
  flex: 1,
  background: theme.CorporateTheme.colors.panelBG,
  border: `1px dashed ${theme.CorporateTheme.colors.border}`,
  borderRadius: CorporateTheme.borderRadius,
  padding: '1rem',
  overflowY: 'auto',
  maxHeight: '420px',
}));

const RightPanel = styled.div`
  flex: 1;
  overflow-y: auto;
  max-height: 400px;
`;

const Title = styled.h2`
  font-size: 1.25rem;
  font-weight: 600;
  margin-bottom: 0.5rem;
`;

const SearchInput = styled.input`
  width: 100%;
  padding: 0.5rem;
  margin-bottom: 1rem;
  border: 1px solid ${({ theme }) => theme.CreateFormTheme.colors.inputBorders};
  border-radius: 6px;
  background: ${({ theme }) => theme.CreateFormTheme.colors.inputBackground};
  color: ${({ theme }) => theme.CreateFormTheme.colors.placeHolder};
`;

const CheckboxList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

export const CheckboxItem = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  fontSize: '0.95rem',
  color: `${theme.CorporateTheme.colors.secondary}`,

  input: {
    appearance: 'none',
    width: '16px',
    height: '16px',
    border: `1.2px solid ${theme.CorporateTheme.colors.headerColor}`,
    borderRadius: '3px',
    position: 'relative',
    cursor: 'pointer',

    '&:checked::after': {
      content: '""',
      position: 'absolute',
      top: '2px',
      left: '5px',
      width: '4px',
      height: '8px',
      border: `solid ${theme.CorporateTheme.colors.headerColor}`,
      borderWidth: '0 2px 2px 0',
      transform: 'rotate(45deg)',
    },
  },
  span: {
    color: `${theme.CorporateTheme.colors.secondary}`,
  }
}));

const SelectAllContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  margin-bottom: 0.5rem;
  border-bottom: 1px solid ${CorporateTheme.colors.border};
`;

const Footer = styled.div`
  margin-top: 1rem;
  display: flex;
  justify-content: flex-end;
  gap: 1rem;
`;

interface ColumnIdentifierProps {
  onClose: () => void;
  onSave?: (dynamicColumns: Column[], selectedColumns: string[]) => void;
  availableColumns: string[];
  initialSelectedColumns: string[];
  entityType: string;
}

interface Column {
  field?: string;
  id: string;
  label: string;
  isSortable?: boolean;
  isFlag?: boolean;
  dataType?: string;
}

const ColumnIdentifier: React.FC<ColumnIdentifierProps> = ({
  onClose,
  onSave,
  availableColumns,
  initialSelectedColumns,
  entityType,
}) => {
  const { theme } = useTheme();
  const [columns, setColumns] = useState<Column[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isInitialLoading, setIsInitialLoading] = useState(true);
  const [isFetchingMore, setIsFetchingMore] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedFields, setSelectedFields] = useState<string[]>(
    initialSelectedColumns
  );
  const [isLoading, setIsLoading] = useState(true);

  const leftPanelRef = useRef<HTMLDivElement>(null);
  const isLoadingNextPage = useRef(false);

  useEffect(() => {
    const el = leftPanelRef.current;
    if (!el) return;

    const handleScroll = () => {
      if (!hasMore || isFetchingMore || isLoadingNextPage.current) return;

      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) {
        isLoadingNextPage.current = true;
        setPage(prev => prev + 1);
      }
    };

    el.addEventListener("scroll", handleScroll);
    return () => el.removeEventListener("scroll", handleScroll);
  }, [hasMore, isFetchingMore]);

  useEffect(() => {
    if (!isFetchingMore) {
      isLoadingNextPage.current = false;
    }
  }, [isFetchingMore]);

  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    // setPage(1);
    // setColumns([]);
    // setHasMore(true);
    debouncedSearch(value);
  };

  const fetchDynamicColumns = async (query?: string, reset: boolean = false) => {
    try {
      if (reset) {
        setPage(1);
        setColumns([]);
        setHasMore(true);
      }
      // setIsLoading(true);
      if (page === 1) {
        setIsInitialLoading(true);
      } else {
        setIsFetchingMore(true);
      }
      const dynamicColumns = entityType === 'contacts' || entityType === 'relationships'
        ? await contactService.getDynamicColumns('contact', query ?? searchTerm, page)
        : await organizationsService.getDynamicColumns('organization', query ?? searchTerm, page);

      let transformedColumns: Column[] = [];

      if (entityType === 'contacts' || entityType === 'relationships') {
        transformedColumns = dynamicColumns?.items
          ?.map((col: { label: string; field: string; dataType: string }) => ({
            id: col.field || col.label,
            label: col.label == 'First Name' ? 'Name' : col.label,
            isSortable: true,
            dataType: col.dataType,
          }));
      } else {
        transformedColumns = dynamicColumns?.items?.map(
          (col: { label: string; field: string; dataType: string; }) => ({
            id: col.field,
            label: col.label,
            isSortable: true,
            dataType: col.dataType,
          })
        );
      }

      if (page === 1) {
        setColumns(transformedColumns);
      } else {
        setColumns(prev => [...prev, ...transformedColumns]);
      }

      setHasMore(columns.length + transformedColumns.length < dynamicColumns.total);

    } catch (error) {
      console.error("Failed to load columns:", error);
    } finally {
      setIsInitialLoading(false);
      setIsFetchingMore(false);
    }
  };

  useEffect(() => {


    fetchDynamicColumns();
  }, [entityType, page]);

  const debouncedSearch = useCallback(
    debounce((query: string) => {
      fetchDynamicColumns(query); // your API call
    }, 500), // wait 500ms after typing
    [entityType]
  );

  useEffect(() => {
    setSelectedFields(initialSelectedColumns);
  }, [initialSelectedColumns]);

  const handleSelect = (field: string) => {
    if (!selectedFields.includes(field)) {
      setSelectedFields([...selectedFields, field]);
    }
  };

  const handleDeselect = (field: string) => {
    setSelectedFields(selectedFields.filter((f) => f !== field));
  };

  const handleCheckboxChange = (field: string, checked: boolean) => {
    if (checked) handleSelect(field);
    else handleDeselect(field);
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allFieldIds = filteredFields.map(field => field.id);
      setSelectedFields(Array.from(new Set([...selectedFields, ...allFieldIds])));
    } else {
      const filteredFieldIds = filteredFields.map(field => field.id);
      setSelectedFields(selectedFields.filter(f => !filteredFieldIds.includes(f)));
    }
  };
  const filteredFields = columns;
  const isAllSelected = filteredFields.length > 0 &&
    filteredFields.every(field => selectedFields.includes(field.id));

  return (
    <>
      <HeaderContainer>
        <HeaderTitles>
          <StyledText
            className="mb-2"
            fontSize="20px"
            fontWeight={600}
            textColor={theme.CorporateTheme.colors.primary}
          >
            Column Identifier
          </StyledText>
          <StyledText
            fontSize="14px"
            fontWeight={500}
            textColor={theme.CorporateTheme.colors.textSecondary}
          >
            Customize the columns displayed in your list view. Select and
            reorder the fields you want to see. Changes will be saved
            automatically.
          </StyledText>
        </HeaderTitles>
        <button
          onClick={onClose}
          style={{
            background: "none",
            border: "none",
            cursor: "pointer",
            padding: 0,
            position: "absolute",
            top: "0",
            right: "0",
          }}
        >
          <CloseIcon color={theme.CorporateTheme.colors.textSecondary} />
        </button>
      </HeaderContainer>

      <Container>
        <LeftPanel ref={leftPanelRef} theme={theme}>
          <StyledText
            className="mt-1 mb-3"
            fontSize="14px"
            fontWeight={600}
            textColor={theme.CorporateTheme.colors.headerColor}
          >
            Available Columns
          </StyledText>

          <SearchInput
            theme={theme}
            type="text"
            placeholder="Search columns..."
            value={searchTerm}
            onChange={(e) => handleSearchChange(e.target.value)}
          />

          {isInitialLoading ? (
            <>
              {/* <SkeletonCell
                style={{
                  height: "32px",
                  marginBottom: "1rem",
                  borderRadius: "6px",
                }}
              /> */}
              <CheckboxList>
                {[1, 2, 3, 4, 5].map((i) => (
                  <SkeletonCell
                    key={i}
                    style={{
                      height: "20px",
                      margin: "4px 0",
                      borderRadius: "3px",
                    }}
                  />
                ))}
              </CheckboxList>
            </>
          ) : (
            <>

              {filteredFields.length > 0 ? (
                <SelectAllContainer>
                  <CheckboxItem theme={theme}>
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      onChange={(e) => handleSelectAll(e.target.checked)}
                    />
                    <StyledText
                      fontSize="14px"
                      fontWeight={600}
                      textColor={theme.CorporateTheme.colors.headerColor}
                    >
                      Select All
                    </StyledText>
                  </CheckboxItem>
                  <StyledText
                    fontSize="12px"
                    fontWeight={500}
                    textColor={theme.CorporateTheme.colors.textSecondary}
                  >
                    {selectedFields.length} selected
                  </StyledText>
                </SelectAllContainer>
              ) : <div className='d-flex flex-column align-items-center justify-content-center gap-2'>
                <NoDataIcon
                  style={{
                    width: "180px",
                    height: "130px",
                  }}
                />
                <StyledText fontSize="16px">No columns found</StyledText>
              </div>}
              <CheckboxList>
                {filteredFields.filter(field => !(field?.id === 'c.id' || field?.id === 'o.id')).map(field => (
                  <CheckboxItem theme={theme} key={field.id} style={{ paddingBottom: '10px', borderBottom: `1px solid ${theme.CorporateTheme.colors.border}` }}>
                    <input
                      type="checkbox"
                      checked={selectedFields.includes(field.id)}
                      onChange={e => handleCheckboxChange(field.id, e.target.checked)}
                    />
                    <div className='col'>
                      <StyledText
                        fontSize="14px"
                        fontWeight={500}
                        textColor={theme.CorporateTheme.colors.textSecondary}
                        className="mt-1 mb-1"
                      >
                        {formatColumnLabel(field.label)}
                      </StyledText>
                      <div className='d-flex align-items-center justify-content-between'>
                        <StyledText
                          fontSize='12px'
                        >{field.id}</StyledText>
                        <StyledText
                          fontSize='12px'
                          textColor={theme.CorporateTheme.colors.textSecondary}
                        >{field?.dataType}</StyledText>
                      </div>
                    </div>
                  </CheckboxItem>
                ))}
              </CheckboxList>

              {isFetchingMore && (
                <div style={{ textAlign: "center", padding: "8px" }}>
                  <StyledText fontSize="12px" textColor={theme.CorporateTheme.colors.textSecondary}>
                    Loading more...
                  </StyledText>
                </div>
              )}
            </>
          )}
        </LeftPanel>

        <RightPanel>
          <StyledText
            className="mt-1 mb-3"
            fontSize="14px"
            fontWeight={600}
            textColor={theme.CorporateTheme.colors.headerColor}
          >
            Selected Columns
          </StyledText>

          {isInitialLoading ? (
            <div className="d-flex flex-column">
              {[1, 2, 3].map((i) => (
                <SkeletonCell
                  key={i}
                  style={{
                    height: "40px",
                    margin: "8px 0",
                    borderRadius: CorporateTheme.borderRadius,
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="d-flex flex-column">
              {selectedFields.map((field) => (
                <StyledCustomFieldBar
                  theme={theme}
                  key={field}
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "0 0.5rem",
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    {/* <DnDIcon /> */}
                    <StyledText
                      fontSize="12px"
                      fontWeight={600}
                      textColor={theme.CorporateTheme.colors.lightSecondary}
                    >
                      {formatColumnLabel(columns.find(c => c.id === field)?.label || field)}
                    </StyledText>
                  </div>{field !== 'c.id' && field !== 'o.id' && field !== 'contact_id' && field !== 'organization_id' &&
                    <button
                      onClick={() => handleDeselect(field)}
                      style={{
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      <CloseIcon color={theme.CorporateTheme.colors.textSecondary} />
                    </button>}
                </StyledCustomFieldBar>
              ))}
            </div>
          )}
        </RightPanel>
      </Container>

      <Footer>
        <Button theme={theme} $variant="cancel" onClick={onClose}>
          <CloseIcon color={theme.CorporateTheme.colors.textSecondary} />
          Cancel
        </Button>
        <Button
          theme={theme}
          onClick={() =>
            onSave &&
            onSave(selectedFields.length > 0 ? columns : [], selectedFields)
          }
          type="submit"
          $variant="add"
        >
          <SaveIcon color={theme.CorporateTheme.colors.background} />
          Save Configuration
        </Button>
      </Footer>
    </>
  );
};

export default ColumnIdentifier;